<!-- @/pages/stake/index.vue -->

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { INSTRUCTION_TEXTS } from "@/utils/constant";
// import generalBackground from "@/assets/images/general-background.png";
import StakingInfoPanel from "@/components/stake/StakingInfoPanel.vue";
import StakeTabs from "@/components/stake/StakeTabs.vue";
import StakeGrid from "@/components/stake/StakeGrid.vue";
// import NexusTab from "@/components/stake/NexusTab.vue";
import RewardsPanel from "@/components/stake/RewardsPanel.vue";
import TransferFromOldWalletDialog from "@/components/Dialog/TransferFromOldWalletDialog.vue";
// Import data mapper utility
import { mapNftToStandardFormat } from "@/utils/nfts/nftDataMapper";
// Import stores
import { useDojoStakingStore } from "@/plugins/2.pinia/modules/nfts/dojoStakingStore";
import { useStakedVrumblersStore } from "@/plugins/2.pinia/modules/nfts/stakedVrumblersStore";
import { useUnstakedVrumblersStore } from "@/plugins/2.pinia/modules/nfts/unstakedVrumblersStore";
import { useNftDialogStore } from "@/plugins/2.pinia/modules/nfts/nftDialogStore.js";
import { useAuthStore } from "@/plugins/2.pinia/modules/auth";
import { useDisplay } from "vuetify";
import { useSujiRewardsStore } from "@/plugins/2.pinia/modules/suji/sujiRewardsStore";

definePage({
  name: "Stake",
  meta: {
    layout: "default",
    requiresAuth: true,
    title: "Stake NFTs",
    description: "Stake your Vrumblers to earn $SUJI and $NEXUS rewards and manage mystics.",
  },
});

// Store imports
const dojoStakingStore = useDojoStakingStore();
const stakedStore = useStakedVrumblersStore();
const unstakedVrumblersStore = useUnstakedVrumblersStore();
const onboarding = useOnboardingStore();
const authStore = useAuthStore();
const router = useRouter();
const { mobile } = useDisplay(); // ✨ NEW
const sujiRewardsStore = useSujiRewardsStore();
const nftDialogStore = useNftDialogStore();

// Reactive state
const activeTab = ref("MYSTICS"); // Default tab

const showTransferDialog = ref(false);

// Rewards data
const rewards = ref({
  $SUJI: 0,
  $NEXUS: 0,
});

// Define tabs for the top-level tabs
const mainTabs = [
  { id: "MYSTICS", label: "Mystics" },
  // { id: "$NEXUS", label: "$NEXUS" },
];

// Connect to viewMode in dojoStakingStore
const stakingTab = computed({
  get: () => dojoStakingStore.viewMode,
  set: (value) => dojoStakingStore.setViewMode(value),
});

const normalizedStakedNfts = computed(() => {
  return stakedStore.stakedVrumblersArray?.map((nft) =>
    mapNftToStandardFormat(nft, true)
  );
});

// Active Vrumblers based on current view mode
const activeNfts = computed(() => {
  return stakingTab.value === "UNSTAKED"
    ? unstakedVrumblersStore.normalizedUnstakedVrumblers
    : normalizedStakedNfts.value;
});

// Computed property for overall loading state
const isLoadingAny = computed(() => {
  return (
    unstakedVrumblersStore.isLoading ||
    stakedStore.isLoading ||
    dojoStakingStore.isProcessing
  );
});

// Simplified computed properties
const unstakedCount = computed(
  () => unstakedVrumblersStore.unstakedVrumblersCount
);
const stakedCount = computed(() => normalizedStakedNfts.value?.length || 0);

// Handle staking tab change
const handleStakingTabChange = (tabId) => {
  stakingTab.value = tabId;
};

// Handle main tab change
const handleMainTabChange = (tabId) => {
  activeTab.value = tabId;
};

// OPEN THE TRANSFER DIALOG
const handleOpenTransferDialog = () => {
  console.log("Opening old wallet transfer dialog");
  showTransferDialog.value = true;
};

// TRANSFER COMPLETION
const handleTransferComplete = async (transferredTokenIds) => {
  console.log("Transfer completed with token IDs:", transferredTokenIds);

  console.log(
    `Successfully transferred ${transferredTokenIds.length} Vrumblers to your account`
  );

  // Refresh the NFT data to reflect the transfers
  await fetchNftData();
};

// Stake selected NFTs
const stakeSelectedNfts = async () => {
  if (dojoStakingStore.selectedTokenIdsToStake.length === 0) {
    console.warn("No NFTs selected for staking");
    return;
  }

  // Check wallet authorization before proceeding
  if (!dojoStakingStore.isWalletAuthorized()) {
    console.error("Wallet not authorized. Please reconnect your wallet.");
    dojoStakingStore.handleAuthorizationFailure();
    return;
  }

  try {
    // Store the count before staking (since it will be cleared after success)
    const countToStake = dojoStakingStore.selectedTokenIdsToStake.length;

    console.log(`Staking ${countToStake} NFTs...`);

    // Execute staking transaction using dojoStakingStore
    const result = await dojoStakingStore.stakeSelectedTokens();

    if (result) {
      // Use the stored count for the success message
      console.log(`Successfully staked ${countToStake} NFTs`);
      try {
        await sujiRewardsStore.refreshRewards();
      } catch (refreshError) {
        console.error("Failed to refresh $NEXUS rewards:", refreshError);
      }
    }
  } catch (err) {
    console.error("Staking error:", err);
  }
};

// Unstake selected NFTs
const unstakeSelectedNfts = async () => {
  if (dojoStakingStore.selectedTokenIdsToUnstake.length === 0) {
    console.warn("No NFTs selected for unstaking");
    return;
  }

  // Check wallet authorization before proceeding
  if (!dojoStakingStore.isWalletAuthorized()) {
    console.error("Wallet not authorized. Please reconnect your wallet.");
    dojoStakingStore.handleAuthorizationFailure();
    return;
  }

  try {
    // Store the count before unstaking (since it will be cleared after success)
    const countToUnstake = dojoStakingStore.selectedTokenIdsToUnstake.length;

    console.log(`Unstaking ${countToUnstake} NFTs...`);

    // Execute unstaking transaction using dojoStakingStore
    const result = await dojoStakingStore.unstakeSelectedTokens();

    if (result) {
      // Use the stored count for the success message
      console.log(`Successfully unstaked ${countToUnstake} NFTs`);
      try {
        await sujiRewardsStore.refreshRewards();
      } catch (refreshError) {
        console.error("Failed to refresh $NEXUS rewards:", refreshError);
      }
    }
  } catch (err) {
    console.error("Unstaking error:", err);
  }
};

// Handle NFT info display
const handleNftInfo = (itemId) => {
  if (!itemId) {
    console.error("Invalid NFT identifier for info dialog:", itemId);
    return;
  }

  nftDialogStore.openInventoryDialog({
    id: itemId,
    tab: "ALL",
  });
};

// Open marketplace dialog
const openMarketplace = () => {
  nftDialogStore.openMarketDialog({ tab: "ALL" });
};

// Handle selection change from StakeGrid
const handleSelectionChange = (selection) => {
  console.log("Selection changed:", selection);

  // Update our selection in the dojoStakingStore
  if (selection.unstaked) {
    dojoStakingStore.clearStakeSelection();
    selection.unstaked.forEach((tokenId) => {
      dojoStakingStore.addToStakeSelection(tokenId);
    });
  }

  if (selection.staked) {
    dojoStakingStore.clearUnstakeSelection();
    selection.staked.forEach((tokenId) => {
      dojoStakingStore.addToUnstakeSelection(tokenId);
    });
  }
};

// Fetch NFT data
const fetchNftData = async () => {
  try {
    await Promise.all([
      unstakedVrumblersStore.fetchUnstakedVrumblers(),
      stakedStore.fetchStakedVrumblers(),
    ]);

    console.log(
      `Loaded ${unstakedCount.value} unstaked NFTs and ${stakedCount.value} staked NFTs`
    );
  } catch (error) {
    console.error("Failed to load NFT data:", error);

    // Check if this is an auth error
    if (
      error.message?.includes("No wallet connected") ||
      error.message?.includes("Wallet authorization") ||
      error.message?.includes("Local Key not found")
    ) {
      dojoStakingStore.handleAuthorizationFailure();
    }
  }
};

// Watch for store errors
watch(
  () => dojoStakingStore.stakingError,
  (error) => {
    if (error) {
      console.error("Staking error from store:", error);
    }
  }
);

watch(
  () => dojoStakingStore.unstakingError,
  (error) => {
    if (error) {
      console.error("Unstaking error from store:", error);
    }
  }
);

// Watch for auth connection status changes
watch(
  () => authStore.isConnectedPN,
  (isConnected) => {
    if (isConnected) {
      // Refresh data when reconnected
      fetchNftData();
    }
  },
  { immediate: true }
);

// Initialize the stores and fetch data
onMounted(async () => {
  if (mobile.value) {
    // Redirect to first mobile sub-page (Mystics)
    await router.push("/stake/mobile-mystics");
    return;
  }

  await nextTick();

  if (!onboarding.shown.stake) {
    onboarding.showInstruction({
      text: INSTRUCTION_TEXTS.stake,
      onClose: () => onboarding.markShown("stake"),
    });
  }
});

// Update stakingTabs labels with counts
const formattedStakingTabs = computed(() => {
  return [
    { id: "UNSTAKED", label: `UNSTAKED (${unstakedCount.value})` },
    { id: "STAKED", label: `STAKED (${stakedCount.value})` },
  ];
});
</script>

<template>
  <div>
    <!-- StarrySky as fixed background -->
    <StarrySky class="" />

    <div class="tw-page-container tw-min-h-screen tw-relative tw-z-10">
      <v-container>
        <!-- Main two-column layout -->
        <div
          class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 xl:tw-grid-cols-[9fr_11fr] tw-gap-y-4"
        >
          <div class="">
            <my-profile-block />

            <loadout-block id="loadout-block" />
          </div>

          <!-- Left Column - Staking Info -->

          <!-- Right Column - Tabs and Content -->
          <v-sheet color="transparent" class="">
            <v-card color="transparent" class="!tw-rounded-2xl !tw-p-0" flat>
              <BackgroundBlock class="tw-top-7" />
              <!-- Main Tabs Component -->
              <StakeTabs
                :activeTab="activeTab"
                :tabs="mainTabs"
                variant="primary"
                @change-tab="handleMainTabChange"
              />

              <v-card-text>
                <!-- Staking Tabs (only shown for MYSTICS tab) -->
                <div class="tw-h-[50px]">
                  <!-- This fixed height ensures consistent spacing -->
                  <StakeTabs
                    v-if="activeTab === 'MYSTICS'"
                    :activeTab="stakingTab"
                    :tabs="formattedStakingTabs"
                    variant="secondary"
                    @change-tab="handleStakingTabChange"
                  />
                  <!-- Empty placeholder when on $NEXUS tab -->
                  <div v-else class="tw-h-full"></div>
                </div>

                <!-- Two-column sub-layout for the content area -->
                <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-3 tw-gap-4">
                  <!-- Left sub-column - NFT Display -->
                  <div class="tw-flex tw-flex-col tw-gap-4 xl:tw-col-span-2">
                    <!-- NFT Display -->
                    <div class="tw-rounded-lg tw-p-0 tw-flex-1">
                      <!-- Tab content -->
                      <StakeGrid
                        v-if="activeTab === 'MYSTICS'"
                        :items="activeNfts"
                        :activeTab="stakingTab"
                        :loading="isLoadingAny"
                        :mode="stakingTab === 'STAKED' ? 'unstake' : 'stake'"
                        @item-info="handleNftInfo"
                        @selection-change="handleSelectionChange"
                      />

                      <!-- Action buttons centered under StakeGrid - always visible but state changes based on selection -->
                      <div
                        v-if="activeTab === 'MYSTICS'"
                        class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mt-6 tw-mb-4"
                      >
                        <!-- Stake button - shown when in UNSTAKED view -->
                        <v-btn
                          v-if="stakingTab === 'UNSTAKED'"
                          color="undefined"
                          rounded="xl"
                          variant="flat"
                          :loading="dojoStakingStore.isStaking"
                          :disabled="!dojoStakingStore.hasStakeSelection"
                          @click="stakeSelectedNfts"
                          class="btn-stake tw-px-8"
                        >
                          <div class="btn-overlay"></div>
                          <div class="tw-z-[1] tw-font-bold">
                            {{
                              dojoStakingStore.hasStakeSelection
                                ? "Stake"
                                : "Select"
                            }}
                            {{
                              dojoStakingStore.hasStakeSelection
                                ? dojoStakingStore.selectedTokenIdsToStake
                                    .length
                                : ""
                            }}
                            {{
                              dojoStakingStore.hasStakeSelection
                                ? dojoStakingStore.selectedTokenIdsToStake
                                    .length !== 1
                                  ? "NFTs"
                                  : "NFT"
                                : "NFTs"
                            }}
                          </div>
                        </v-btn>

                        <!-- Unstake button - shown when in STAKED view -->
                        <v-btn
                          v-if="stakingTab === 'STAKED'"
                          color="undefined"
                          rounded="xl"
                          variant="flat"
                          :loading="dojoStakingStore.isUnstaking"
                          :disabled="!dojoStakingStore.hasUnstakeSelection"
                          @click="unstakeSelectedNfts"
                          class="btn-unstake tw-px-8"
                        >
                          <div class="btn-overlay"></div>
                          <div class="tw-z-[1] tw-font-bold">
                            {{
                              dojoStakingStore.hasUnstakeSelection
                                ? "Unstake"
                                : "Select"
                            }}
                            {{
                              dojoStakingStore.hasUnstakeSelection
                                ? dojoStakingStore.selectedTokenIdsToUnstake
                                    .length
                                : ""
                            }}
                            {{
                              dojoStakingStore.hasUnstakeSelection
                                ? dojoStakingStore.selectedTokenIdsToUnstake
                                    .length !== 1
                                  ? "NFTs"
                                  : "NFT"
                                : "NFTs"
                            }}
                          </div>
                        </v-btn>

                        <v-btn
                          color="primary"
                          rounded="pill"
                          variant="flat"
                          class="tw-font-semibold tw-text-xs tw-tracking-wide tw-px-6 tw-text-slate-900 tw-bg-gradient-to-r tw-from-amber-400 tw-via-orange-500 tw-to-rose-500 tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                          @click="openMarketplace"
                        >
                          <v-icon
                            icon="mdi-storefront-outline"
                            size="18"
                            class="tw-mr-2"
                          />
                          Open Marketplace
                        </v-btn>
                        <!-- Stake89 button - only visible in dev mode -->
                      </div>

                      <!-- <NexusTab
                    v-else-if="activeTab === '$NEXUS'"
                    :rewards="rewards"
                  /> -->
                    </div>
                  </div>

                  <div>
                    <RewardsPanel
                      :activeTab="activeTab"
                      :rewards="rewards"
                      :stakedMysticsCount="stakedCount"
                    />

                    <StakingInfoPanel
                      class="tw-mb-6"
                      @open-old-wallet-dialog="handleOpenTransferDialog"
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-sheet>
        </div>
      </v-container>
    </div>

    <!-- OLD WALLET TRANSFER DIALOG -->
    <TransferFromOldWalletDialog
      v-model="showTransferDialog"
      @transfer-complete="handleTransferComplete"
    />
  </div>
</template>

<style lang="postcss" scoped>
.btn-unstake {
  background: linear-gradient(120deg, #eb2b7d 0%, #f15323 100%);
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(20px);
  .v-btn__overlay {
    @apply !tw-opacity-0;
  }
  .btn-overlay {
    background: linear-gradient(90deg, #eb2b7d 0.24%, #f15323 99.78%);
    filter: blur(10px) drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
  &.v-btn--disabled {
    background: rgba(128, 128, 128, 0.5);
    .btn-overlay {
      display: none;
    }
  }
}

.btn-stake {
  background-color: rgba(5, 248, 0, 0.76);

  box-shadow: 2px 2px 4px 0px rgba(0, 0, 0, 0.25);
  /* color: white; */
  .v-btn__overlay {
    @apply !tw-opacity-0;
  }
  .btn-overlay {
    background: rgba(5, 248, 0, 0.76);
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
    filter: blur(10px);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
  &.v-btn--disabled {
    background-color: rgba(128, 128, 128, 0.5);
    .btn-overlay {
      display: none;
    }
  }
}
</style>
